import { useCallback, useEffect, useMemo } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { AlertCircle, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { BackToLandingLink } from "@/components/layout/BackToLandingLink";
import { StandardPageShell } from "@/components/layout/StandardPageShell";
import { ModuleAnswerReviewContent } from "@/components/results/ModuleAnswerReviewContent";
import { useModuleAnswers } from "@/hooks/useModuleAnswers";
import { useSimulationResults } from "@/hooks/useSimulationResults";
import { ROUTES } from "@/lib/routeConstants";
import { isModuleReviewAvailable } from "@/lib/analysisStatus";
import type { ModuleResult } from "@/types/simulation-results";

const REVIEW_MODULES: ModuleResult["module"][] = [
  "reading",
  "writing",
  "listening",
  "speaking",
];

function isReviewModule(value: string): value is ModuleResult["module"] {
  return REVIEW_MODULES.includes(value as ModuleResult["module"]);
}

export default function ModuleResultReview() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sessionId, moduleName } = useParams<{
    sessionId: string;
    moduleName: string;
  }>();
  const [searchParams] = useSearchParams();

  const {
    results,
    isLoading: isResultsLoading,
    error: resultsError,
  } = useSimulationResults("all", "all");

  const activeModule = useMemo(() => {
    const normalized = moduleName?.toLowerCase() ?? "";
    return isReviewModule(normalized) ? normalized : "";
  }, [moduleName]);

  const sessionResult = useMemo(
    () => results.find((result) => result.id === sessionId),
    [results, sessionId],
  );

  const {
    answers,
    isLoading: isAnswersLoading,
    error: answersError,
    fetchAnswers,
  } = useModuleAnswers({
    sessionModules: sessionResult?.modules ?? [],
    activeModule,
    sessionId,
  });

  const activeModuleResult = useMemo(
    () =>
      sessionResult?.modules.find((module) => module.module === activeModule) ??
      null,
    [sessionResult, activeModule],
  );

  const canReviewActiveModule = useMemo(
    () =>
      activeModuleResult ? isModuleReviewAvailable(activeModuleResult) : false,
    [activeModuleResult],
  );

  const firstGradedModule = useMemo(
    () =>
      sessionResult?.modules.find((module) => isModuleReviewAvailable(module))
        ?.module ?? null,
    [sessionResult],
  );

  const returnTo = useMemo(() => {
    const requestedReturnTo = searchParams.get("returnTo");
    if (
      requestedReturnTo?.startsWith("/") &&
      !requestedReturnTo.startsWith("//")
    ) {
      return requestedReturnTo;
    }
    return sessionId
      ? `${ROUTES.RESULTS}?session=${encodeURIComponent(sessionId)}`
      : ROUTES.RESULTS;
  }, [searchParams, sessionId]);

  const returnToQuery = useMemo(
    () => `?returnTo=${encodeURIComponent(returnTo)}`,
    [returnTo],
  );

  useEffect(() => {
    if (!sessionId) return;

    if (!activeModule) {
      if (firstGradedModule) {
        navigate(`/results/${sessionId}/${firstGradedModule}${returnToQuery}`, {
          replace: true,
        });
      }
      return;
    }

    if (!canReviewActiveModule) {
      return;
    }

    void fetchAnswers(sessionId, activeModule);
  }, [
    activeModule,
    canReviewActiveModule,
    fetchAnswers,
    firstGradedModule,
    navigate,
    returnToQuery,
    sessionId,
  ]);

  const handleSessionModuleChange = useCallback(
    (nextModule: string) => {
      if (!sessionId) return;
      navigate(`/results/${sessionId}/${nextModule}${returnToQuery}`);
    },
    [navigate, returnToQuery, sessionId],
  );

  const headerAction = <BackToLandingLink to={returnTo} />;

  const renderError = (message: string) => (
    <Card className="border-destructive/30 bg-destructive/5">
      <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
        <AlertCircle className="h-10 w-10 text-destructive" />
        <p className="font-ui text-sm text-foreground">{message}</p>
        <Button asChild variant="outline">
          <Link to={returnTo}>
            {t("simulation.results.errors.tryAgain", {
              defaultValue: "Try again",
            })}
          </Link>
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <StandardPageShell
      title=""
      headerRight={<LanguageSwitcher />}
      className="mx-auto flex min-h-0 w-full max-w-[96rem] flex-1 flex-col px-0 py-0 sm:px-6 sm:py-6 lg:px-8"
    >
      {isResultsLoading && !sessionResult ? (
        <div className="flex flex-col items-center justify-center p-12">
          <Loader2 className="mb-4 h-10 w-10 animate-spin text-primary" />
          <p className="font-ui animate-pulse text-muted-foreground">
            {t("common.loading")}
          </p>
        </div>
      ) : resultsError ? (
        renderError(resultsError.message)
      ) : answersError ? (
        renderError(answersError)
      ) : !sessionId || !activeModule ? (
        renderError(
          t("simulation.results.errors.unableToLoad", {
            defaultValue: "Unable to load results",
          }),
        )
      ) : !canReviewActiveModule ? (
        <Card className="border-amber-200/60 bg-amber-50/50">
          <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
            <Loader2 className="h-10 w-10 animate-spin text-amber-600" />
            <p className="font-ui text-sm text-foreground">
              {t("simulation.results.status.analysing", {
                defaultValue: "Analysing...",
              })}
            </p>
            <p className="font-ui text-xs text-muted-foreground">
              {t("simulation.results.reviewDialog.analysingHint", {
                defaultValue:
                  "AI is reviewing your answers for this section. Results will be available shortly.",
              })}
            </p>
            <Button asChild variant="outline">
              <Link to={returnTo}>
                {t("common.back", { defaultValue: "Back" })}
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <ModuleAnswerReviewContent
          layout="page"
          sessionId={sessionId}
          moduleName={activeModule}
          answers={answers}
          isLoading={isAnswersLoading}
          sessionModules={sessionResult?.modules ?? []}
          onSessionModuleChange={handleSessionModuleChange}
          headerAction={headerAction}
        />
      )}
    </StandardPageShell>
  );
}
